import { useState, useEffect } from 'react';
import { Box, useMediaQuery, useTheme, IconButton, AppBar, Toolbar, Typography } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import BillingMenu, { DRAWER_WIDTH } from '../../components/billing/BillingMenu';

// Sales Components
import ConsumerFinal from '../../components/billing/sales/ConsumerFinal';
import CCF from '../../components/billing/sales/CCF';
import Retention from '../../components/billing/sales/Retention';
import ExcludedSubject from '../../components/billing/sales/ExcludedSubject';
import CreditNote from '../../components/billing/sales/CreditNote';
import DebitNote from '../../components/billing/sales/DebitNote';

// Purchase Components
import PurchasesCF from '../../components/billing/purchases/PurchasesCF';
import PurchasesCCF from '../../components/billing/purchases/PurchasesCCF';

// Book Components
import BookPurchases from '../../components/billing/books/BookPurchases';
import BookSalesCF from '../../components/billing/books/BookSalesCF';
import BookSalesCCF from '../../components/billing/books/BookSalesCCF';
import DiaryBook from '../../components/billing/books/DiaryBook';
import GeneralLedger from '../../components/billing/books/GeneralLedger';
import FinancialStatements from '../../components/billing/books/FinancialStatements';
import TaxBookPage from '../../components/billing/taxBooks/TaxBookPage';
import PurchaseBooksPage from '../../components/billing/taxBooks/PurchaseBooksPage';
import SalesAnnexesPage from '../../components/billing/taxBooks/SalesAnnexesPage';
import BooksSalesPage from '../../components/billing/taxBooks/BooksSalesPage';
import BooksPurchasesPage from '../../components/billing/taxBooks/BooksPurchasesPage';
import F07AnnexesPage from '../../components/billing/taxBooks/F07AnnexesPage';

// Report Components
import ReportIVA from '../../components/billing/reports/ReportIVA';
import ReportRenta from '../../components/billing/reports/ReportRenta';
import ReportPCTA from '../../components/billing/reports/ReportPCTA';

// New Components - Wizard and Consultas
import DTEWizard from '../../components/billing/dte/DTEWizard';
import DTEAnulacionWizard from '../../components/billing/dte/anulacion/DTEAnulacionWizard';
import DTEByDate from '../../components/billing/consultas/DTEByDate';
import QuickSaleWizard from '../../components/billing/dte/QuickSaleWizard';
import ProductsManager from '../../components/billing/ProductsManager';
import BillingDashboard from '../../components/billing/BillingDashboard';
import SettingsPage from '../Settings/SettingsPage';

const money = (value) => `$${(parseFloat(value || 0) || 0).toFixed(2)}`;

const salesTaxpayerColumns = [
  { key: 'fecha_emision_documento', label: 'Fecha' },
  { key: 'tipo_documento', label: 'Tipo DTE' },
  { key: 'numero_control_interno', label: 'Núm. control' },
  { key: 'nit_o_nrc', label: 'NIT/NRC' },
  { key: 'nombre_razon_social_o_denominacion', label: 'Cliente' },
  { key: 'ventas_gravadas_locales', label: 'Gravadas' },
  { key: 'debito_fiscal', label: 'Débito fiscal' },
  { key: 'total_ventas', label: 'Total' },
];

const annulmentColumns = [
  { key: 'fecha_emision', label: 'Fecha' },
  { key: 'tipo_documento', label: 'Tipo DTE' },
  { key: 'numero_control', label: 'Núm. control' },
  { key: 'tipo_detalle_documento', label: 'Tipo detalle' },
  { key: 'motivo_anulacion', label: 'Motivo' },
];

const f07Columns = [
  { key: 'seccion', label: 'Sección' },
  { key: 'fecha_emision_documento', label: 'Fecha' },
  { key: 'tipo_documento', label: 'Tipo DTE' },
  { key: 'numero_control_interno', label: 'Control' },
  { key: 'nombre_razon_social_o_denominacion', label: 'Nombre' },
  { key: 'monto_gravado', label: 'Monto gravado' },
  { key: 'debito_o_credito_fiscal', label: 'Débito/Crédito' },
  { key: 'total_operacion', label: 'Total operación' },
];

const f14Columns = [
  { key: 'fecha_emision', label: 'Fecha' },
  { key: 'tipo_documento', label: 'Tipo DTE' },
  { key: 'nit_o_documento_sujeto', label: 'Documento' },
  { key: 'nombre_sujeto', label: 'Nombre' },
  { key: 'numero_resolucion', label: 'Resolución / Control' },
  { key: 'serie_documento', label: 'Serie / Sello' },
  { key: 'numero_documento', label: 'Núm. documento' },
  { key: 'monto_sujeto', label: 'Monto sujeto' },
  { key: 'monto_retencion_o_anticipo', label: 'Retención' },
  { key: 'clasificacion', label: 'Clasificación' },
];

const f930Columns = [
  { key: 'fecha_documento', label: 'Fecha' },
  { key: 'nit', label: 'NIT' },
  { key: 'nombre_contribuyente', label: 'Contribuyente' },
  { key: 'modalidad', label: 'Modalidad' },
  { key: 'codigo_documento', label: 'Cód. doc.' },
  { key: 'serie_documento_emitido', label: 'Serie' },
  { key: 'numero_documento_emitido', label: 'Número emitido' },
  { key: 'documento_relacionado', label: 'Documento relacionado' },
  { key: 'monto_sujeto', label: 'Monto sujeto' },
  { key: 'monto_retencion_percepcion_anticipo_iva', label: 'Monto retenido' },
];

const pathToComponent = {
  // Dashboard - Página de inicio
  'billing': <BillingDashboard />,
  // Nuevo DTE - Wizard
  'nuevo-dte': <DTEWizard />,
  // Venta Rápida
  'venta-rapida': <QuickSaleWizard />,
  // Gestión de Productos
  'gestion-productos': <ProductsManager />,
  // Anulaciones
  'anulacion': <DTEAnulacionWizard />,
  // Consultas
  'consulta-fecha': <DTEByDate />,
  // Configuración
  'settings': <SettingsPage />,
  // Ventas
  'consumidor-final': <ConsumerFinal />,
  'ccf': <CCF />,
  'retencion': <Retention />,
  'sujeto-excluido': <ExcludedSubject />,
  'nota-credito': <CreditNote />,
  'nota-debito': <DebitNote />,
  // Compras
  'compras-cf': <PurchasesCF />,
  'compras-ccf': <PurchasesCCF />,
  // Libros
  'libro-compras': <BookPurchases />,
  'libro-ventas-cf': <BookSalesCF />,
  'libro-ventas-ccf': <BookSalesCCF />,
  'libro-diario': <DiaryBook />,
  'libro-mayor': <GeneralLedger />,
  'estados-financieros': <FinancialStatements />,
  // Reportes MH
  'f07-iva': <ReportIVA />,
  'f11-renta': <ReportRenta />,
  'f14-pcta': <ReportPCTA />,
  // Nuevos libros MH
  'libros-ventas': <BooksSalesPage />,
  'libros-compras': <BooksPurchasesPage />,
  'libros-ventas-contribuyentes': <SalesAnnexesPage />,
  'libros-compras-contribuyentes': (
    <PurchaseBooksPage
      title="Libro de Compras a Contribuyentes (Anexo 3)"
      subtitle="Ingreso manual, vista previa y exportación del libro de compras a contribuyentes."
      bookType="compras_contribuyentes"
    />
  ),
  'libros-compras-consumidor-final': (
    <PurchaseBooksPage
      title="Libro de Compras a Consumidor Final"
      subtitle="Ingreso manual, vista previa y exportación del libro de compras a consumidor final."
      bookType="compras_consumidor_final"
    />
  ),
  'libros-anulaciones': (
    <TaxBookPage
      title="Libro de Anulaciones"
      subtitle="Consolidación de documentos anulados para vista previa y exportación."
      previewPath="books/annulments/preview"
      exportPath="books/annulments/export"
      columns={annulmentColumns}
      summaryCards={[
        { key: 'count', label: 'Documentos anulados' },
      ]}
    />
  ),
  'anexos-f07': (
    <F07AnnexesPage />
  ),
  'anexos-f14': (
    <TaxBookPage
      title="Anexo F14"
      subtitle="Vista previa y exportación del anexo F14 para retenciones/anticipo."
      previewPath="annexes/f14/preview"
      exportPath="annexes/f14/export"
      columns={f14Columns}
      summaryCards={[
        { key: 'count', label: 'Filas' },
        { key: 'totalRetencion', label: 'Total retención', format: money },
      ]}
    />
  ),
  'anexos-f930': (
    <TaxBookPage
      title="Anexo F930 v3"
      subtitle="Reporte en pantalla y generación del libro para comprobantes de retención emitidos."
      previewPath="annexes/f930/preview"
      exportPath="annexes/f930/export"
      columns={f930Columns}
      summaryCards={[
        { key: 'count', label: 'Filas' },
        { key: 'totalMontoSujeto', label: 'Monto sujeto', format: money },
        { key: 'totalRetenido', label: 'Monto retenido', format: money },
      ]}
      extraFilters={[
        {
          key: 'status',
          label: 'Estado',
          options: [
            { value: '', label: 'Todos' },
            { value: 'accepted', label: 'Aceptado' },
            { value: 'sent', label: 'Enviado' },
            { value: 'signed', label: 'Firmado' },
          ],
        },
      ]}
    />
  ),
};

const BillingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Resetear el estado del drawer cuando cambia la ruta
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Obtener el path actual para determinar qué componente mostrar
  const currentPath = location.pathname.split('/').pop();
  // Si es la ruta base /billing, mostrar el dashboard
  const selectedItem = currentPath === 'billing' || !currentPath || !Object.keys(pathToComponent).includes(currentPath) 
    ? 'billing' 
    : currentPath;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSelectItem = (itemId) => {
    navigate(`/billing/${itemId}`);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleMobileClose = () => {
    setMobileOpen(false);
  };

  // Render component based on selected menu item
  const renderContent = () => {
    const Component = pathToComponent[selectedItem];
    if (!Component) {
      return <ConsumerFinal />;
    }
    return Component;
  };

  // Verificar si es el wizard de DTE o anulación para aplicar o no el margen del drawer
  // BillingPage ya incluye el BillingMenu dentro del mismo Box, por lo que NO debe haber margen
  const isDTEWizard = true;

  // Render del menú de facturación
  const renderBillingMenu = () => {
    // En desktop, siempre mostrar el menú
    // En móvil, solo mostrar si mobileOpen es true
    return (
      <BillingMenu 
        selectedItem={selectedItem}
        onSelectItem={handleSelectItem}
        mobileOpen={isMobile && mobileOpen}
        onMobileClose={handleMobileClose}
        isMobile={isMobile}
      />
    );
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Menú de Facturación */}
      {renderBillingMenu()}

      {/* Contenido Principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: isMobile ? 1 : 3,
          px: isMobile ? 0.5 : 3,
          width: { md: isDTEWizard ? '100%' : `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: isDTEWizard ? 0 : `${DRAWER_WIDTH}px` },
          mt: 0,
          overflowX: 'hidden',
        }}
      >
        {/* AppBar para móvil */}
        {isMobile && (
          <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
            <Toolbar>
              <IconButton
                color="inherit"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" noWrap component="div">
                Facturación
              </Typography>
            </Toolbar>
          </AppBar>
        )}

        {/* Contenido */}
        <Box sx={{ mt: 0, overflowX: 'hidden' }}>
          {renderContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default BillingPage;
